import React, {
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';

import css from './styles.module.scss';

type THintPlacement = 'left' | 'right' | 'top' | 'bottom';

interface IHintBlockProps {
  showHint: boolean;
  hintText: ReactNode;
  placement?: THintPlacement;
  offset?: number;
  offsetTop?: number;
  containerRef?: RefObject<HTMLElement>;
}

const HintBlock: FC<IHintBlockProps> = ({
  showHint,
  hintText,
  placement = 'left',
  offset = 20,
  offsetTop = 10,
  children,
  containerRef,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);

  const hintRef = useRef<HTMLDivElement>(null);

  const frameRef = useRef<number>();

  const updatePosition = useCallback(() => {
    if (!bodyRef.current || !hintRef.current || !containerRef?.current) {
      return;
    }

    const containerRect = containerRef?.current.getBoundingClientRect();

    const bodyRect = bodyRef.current.getBoundingClientRect();

    const hintRect = hintRef.current.getBoundingClientRect();

    const isVisible =
      bodyRect.bottom > containerRect.top &&
      bodyRect.top < containerRect.bottom &&
      bodyRect.right > containerRect.left &&
      bodyRect.left < containerRect.right;

    if (!showHint || !isVisible) {
      hintRef.current.style.opacity = '0';
      return;
    }

    let left = 0;
    let top = 0;

    switch (placement) {
      case 'left':
        left = bodyRect.left - hintRect.width - offset;
        top = bodyRect.top + offsetTop + (bodyRect.height - hintRect.height) / 2;
        break;

      case 'right':
        left = bodyRect.right + offset;
        top = bodyRect.top +(bodyRect.height - hintRect.height) / 2;
        break;

      case 'top':
        left = bodyRect.left + (bodyRect.width - hintRect.width) / 2;
        top = bodyRect.top - hintRect.height - offset;
        break;

      case 'bottom':
        left = bodyRect.left + (bodyRect.width - hintRect.width) / 2;
        top = bodyRect.bottom + offset;
        break;
      default:
        break;
    }

    left = Math.max(8, Math.min(left, window.innerWidth - hintRect.width - 8));
    top = Math.max(8, Math.min(top, window.innerHeight - hintRect.height - 8));

    hintRef.current.style.opacity = '1';
    hintRef.current.style.transform = `translate3d(${left}px, ${top}px, 0)`;
  }, [offset, placement, showHint]);

  const requestUpdate = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    cancelAnimationFrame(frameRef.current!);

    frameRef.current = requestAnimationFrame(updatePosition);
  }, [updatePosition]);

  useLayoutEffect(() => {
    requestUpdate();

    window.addEventListener('scroll', requestUpdate, true);
    window.addEventListener('resize', requestUpdate);

    const resizeObserver = new ResizeObserver(requestUpdate);

    if (bodyRef.current) {
      resizeObserver.observe(bodyRef.current);
    }

    return () => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cancelAnimationFrame(frameRef.current!);

      resizeObserver.disconnect();

      window.removeEventListener('scroll', requestUpdate, true);
      window.removeEventListener('resize', requestUpdate);
    };
  }, [requestUpdate]);

  return (
    <>
      <div ref={bodyRef} className={css.hintblock__body}>
        {children}
      </div>

      {createPortal(
        <div ref={hintRef} className={css.hintblock__hint}>
          {hintText}
        </div>,
        document.body,
      )}
    </>
  );
};

export default HintBlock;
